import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GiTrophyCup } from 'react-icons/gi';
import { FaTimes } from 'react-icons/fa';
import { useT } from '../../../i18n';
import { formatPrice, formatUsdt } from '../utils/roundMath';

const easeOut = (x) => 1 - (1 - x) ** 3;

/**
 * Price that counts to each new value and flashes green or red with the direction of the change
 */
export const AnimatedPrice = ({ value, className = '' }) => {
  const target = Number(value) || 0;
  const [shown, setShown] = useState(target);
  const [flash, setFlash] = useState(null);
  const from = useRef(target);
  const frame = useRef(null);

  useEffect(() => {
    const start = from.current;
    if (!target || start === target) {
      from.current = target;
      setShown(target);
      return undefined;
    }
    if (start) setFlash(target > start ? 'up' : 'down');
    const began = performance.now();
    const step = (now) => {
      const p = Math.min(1, (now - began) / 650);
      setShown(start + (target - start) * easeOut(p));
      if (p < 1) frame.current = requestAnimationFrame(step);
    };
    frame.current = requestAnimationFrame(step);
    from.current = target;
    const off = setTimeout(() => setFlash(null), 900);
    return () => {
      cancelAnimationFrame(frame.current);
      clearTimeout(off);
    };
  }, [target]);

  return (
    <span
      className={`lb-num inline-block rounded-lg px-1.5 transition-[background-color,box-shadow] duration-700 ${
        flash === 'up'
          ? 'bg-emerald-400/15 shadow-[0_0_24px_-6px_rgba(52,211,153,0.8)]'
          : flash === 'down'
            ? 'bg-rose-400/15 shadow-[0_0_24px_-6px_rgba(251,113,133,0.8)]'
            : 'bg-transparent'
      } ${className}`}
    >
      {formatPrice(shown)}
    </span>
  );
};

/**
 * The live round's price since it started, against the start price (dashed line)
 */
export const Sparkline = ({ points, lock, side }) => {
  const color = side === 'down' ? '#fb7185' : side === 'up' ? '#34d399' : '#fcd34d';
  const geometry = useMemo(() => {
    if (points.length < 2 || !lock) return null;
    const values = [...points, lock];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || 1;
    const y = (v) => 36 - ((v - min) / span) * 32;
    const x = (i) => (i / (points.length - 1)) * 100;
    const line = points.map((v, i) => `${x(i).toFixed(2)},${y(v).toFixed(2)}`).join(' ');
    return { line, area: `0,40 ${line} 100,40`, lockY: y(lock), last: [x(points.length - 1), y(points[points.length - 1])] };
  }, [points, lock]);

  if (!geometry) return <div className="h-12" />;
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" className="h-12 w-full overflow-visible" aria-hidden="true">
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <line x1="0" x2="100" y1={geometry.lockY} y2={geometry.lockY} stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" strokeDasharray="2 2" vectorEffect="non-scaling-stroke" />
      <polygon points={geometry.area} fill="url(#spark-fill)" />
      <polyline points={geometry.line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      <circle cx={geometry.last[0]} cy={geometry.last[1]} r="1.6" fill={color} />
    </svg>
  );
};

const CONFETTI_COLORS = ['#34d399', '#a369d9', '#fcd34d', '#f472b6', '#38bdf8'];

/** A short confetti burst from the bottom of the screen */
const Confetti = () => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 42 }, (_, i) => ({
        id: i,
        x: (Math.random() - 0.5) * 700,
        y: -(260 + Math.random() * 380),
        rotate: (Math.random() - 0.5) * 720,
        delay: Math.random() * 0.15,
        size: 6 + Math.random() * 6,
        color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
      })),
    []
  );
  return (
    <div className="pointer-events-none fixed bottom-24 left-1/2 z-[60]" aria-hidden="true">
      {pieces.map((p) => (
        <motion.span
          key={p.id}
          className="absolute block rounded-sm"
          style={{ width: p.size, height: p.size * 0.5, background: p.color }}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
          animate={{ x: p.x, y: [0, p.y, p.y + 420], opacity: [1, 1, 0], rotate: p.rotate }}
          transition={{ duration: 2.2, delay: p.delay, ease: 'easeOut', times: [0, 0.45, 1] }}
        />
      ))}
    </div>
  );
};

/** "You won" toast with confetti and the collect button */
export const WinToast = ({ win, onClose, collect }) => {
  const t = useT();
  return (
    <AnimatePresence>
      {win && (
        <>
          <Confetti key={`confetti-${win.epoch}`} />
          {/* centred by the wrapper: framer-motion owns the toast's transform */}
          <motion.div
            key={`toast-${win.epoch}`}
            exit={{ opacity: 0, y: 30 }}
            className="pointer-events-none fixed inset-x-0 bottom-6 z-[61] flex justify-center px-4"
          >
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ type: 'spring', stiffness: 320, damping: 26 }}
            className="pointer-events-auto relative w-full max-w-[380px] rounded-2xl border border-emerald-400/40 bg-[#07150f]/95 p-4 shadow-[0_20px_60px_-10px_rgba(52,211,153,0.5)] backdrop-blur"
            role="status"
          >
            <button onClick={onClose} aria-label={t('bvb.close')} className="absolute right-3 top-3 text-white/40 hover:text-white">
              <FaTimes />
            </button>
            <div className="flex items-center gap-3">
              <GiTrophyCup className="h-10 w-10 shrink-0 text-amber-300" />
              <div>
                <p className="font-bold text-white">{t('bvb.winTitle', { epoch: win.epoch })}</p>
                <p className="lb-num text-sm text-emerald-300">{t('bvb.winBody', { amount: formatUsdt(win.payout) })}</p>
              </div>
            </div>
            <div className="mt-3">{collect}</div>
          </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
